import { useState, useEffect, useCallback } from "react";
import { Socket } from "socket.io-client";
import EndpointsEnum from "../../models/EndpointsEnum";
import IUser from "../../models/IUser";
import IUserUI from "../../models/IUserUI";
import ISupplierModel from "../../models/ISupplierModel";
import IVendorModel from "../../models/IVendorModel";
import ISuppliersOnGetUsersDTO from "../../models/DTO/ISuppliersOnGetUsersDTO";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import * as ProductionService from "../../services/ProductionService";

interface UseShopFloorDataProps {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    socket: Socket | null;
    isAdmin: boolean;
}

const DEFAULT_PAGE_SIZE = 25;

export default function useShopFloorData({ userModel, supplierModel, vendorModel, socket, isAdmin }: UseShopFloorDataProps) {
    const [steps, setSteps] = useState<IProductionDashboardStepDTO[]>([]);
    const [activeStep, setActiveStep] = useState<IProductionDashboardStepDTO | null>(null);
    const [serials, setSerials] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [employees, setEmployees] = useState<IUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSerialsLoading, setIsSerialsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const ctx = { userModel, supplierModel, vendorModel };

    const fetchDashboard = useCallback(async () => {
        try {
            const data = await ProductionService.getProductionDashboard(ctx);
            const sorted = [...data.steps].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
            setSteps(sorted);
            return sorted;
        } catch (err) {
            console.error("Failed to fetch production dashboard", err);
            return [];
        }
    }, [userModel.guid, supplierModel.guid]);

    const fetchSerials = useCallback(
        async (orderStateId: number, pg: number, searchTerm: string) => {
            setIsSerialsLoading(true);
            try {
                const data = await ProductionService.getSerialsByStep(ctx, orderStateId, pg, pageSize, searchTerm);
                setSerials(data.serials);
                setTotalCount(data.totalCount);
            } catch (err) {
                console.error("Failed to fetch serials", err);
            } finally {
                setIsSerialsLoading(false);
            }
        },
        [userModel.guid, supplierModel.guid, pageSize]
    );

    const fetchEmployees = useCallback(() => {
        if (!socket || !isAdmin) return;
        socket.emit(EndpointsEnum.SuppliersGetUsers, {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: "",
        });
    }, [socket, userModel.guid, supplierModel.guid, isAdmin]);

    // Initial load
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            const fetchedSteps = await fetchDashboard();
            if (isAdmin) fetchEmployees();

            if (fetchedSteps.length > 0) {
                setActiveStep(fetchedSteps[0]);
            }
            setIsLoading(false);
        };
        init();
    }, []);

    // Listen for employee list response (admin only)
    useEffect(() => {
        if (!socket || !isAdmin) return;

        const onGetUsers = (data: ISuppliersOnGetUsersDTO) => {
            setEmployees(data.users);
        };

        socket.on(EndpointsEnum.SuppliersOnGetUsers, onGetUsers);
        return () => {
            socket.off(EndpointsEnum.SuppliersOnGetUsers, onGetUsers);
        };
    }, [socket, isAdmin]);

    // Debounce the search input so we don't hit the API on every keystroke
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(handle);
    }, [search]);

    // Reset to the first page whenever the search term changes
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch]);

    // Fetch serials when active step, page, page size, or search changes
    useEffect(() => {
        if (activeStep) {
            fetchSerials(activeStep.stepId ?? 0, page, debouncedSearch);
        }
    }, [activeStep?.stepId, page, pageSize, debouncedSearch]);

    // Real-time: refresh when other clients advance serials
    useEffect(() => {
        if (!socket) return;

        const handler = () => {
            refreshData();
        };

        socket.on(EndpointsEnum.ProductionSerialsAdvanced, handler);
        return () => {
            socket.off(EndpointsEnum.ProductionSerialsAdvanced, handler);
        };
    }, [socket, activeStep?.stepId, page, pageSize, debouncedSearch]);

    const refreshData = useCallback(async () => {
        const updatedSteps = await fetchDashboard();
        if (activeStep) {
            await fetchSerials(activeStep.stepId ?? 0, page, debouncedSearch);
        }
        // If active step was removed or changed, select first available
        if (updatedSteps.length > 0 && activeStep && !updatedSteps.find((s) => s.stepId === activeStep.stepId)) {
            setActiveStep(updatedSteps[0]);
        }
    }, [activeStep?.stepId, page, debouncedSearch, fetchDashboard, fetchSerials]);

    return {
        steps,
        activeStep,
        setActiveStep,
        serials,
        totalCount,
        employees,
        isLoading,
        isSerialsLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        search,
        setSearch,
        refreshData,
    };
}
